import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";

import { createDataTestId, createId } from "./utils/helpers";
import Question, { Optional } from "./Question";
import Fieldset from "./Fieldset";
import { JunoLegend } from "./Legend";
import Hint from "./Hint";
import Input from "./Input";
import Label from "./Label";
import ErrorMessage from "./ErrorMessage";

const DateLegend = styled(JunoLegend)`
  padding-bottom: 0.5rem;
`;

/* stylelint-disable property-no-vendor-prefix */
const DateField = styled.div`
  display: inline-block;
  margin-right: var(--juno-space-3);

  label {
    display: block;
  }

  input {
    max-width: 5.4ex;
    padding-right: 0.25em;
  }

  input:disabled {
    font-weight: bold;
  }

  .dateYear {
    max-width: 9ex;
  }
`;

/* stylelint-enable property-no-vendor-prefix */

class DateQuestion extends Component {
  // Build the handler when the event fires, not during render. A wrong onChange
  // prop then fails at the keyboard, instead of in a render loop.
  handleChange = (datePart) => (event) => {
    this.props.onChange(datePart)(event);
  };

  handleBlur = (datePart) => (event) => {
    if (this.props.validate) {
      this.props.validate(datePart)(event.target.value);
    }
  };

  render() {
    // Pass undefined, not the always-truthy handler, so React warns about an
    // editable field that has a value and no onChange.
    const changeHandler = this.props.onChange ? this.handleChange : undefined;

    const componentId = createId("input", this.props.name);
    const testId = createDataTestId("input", this.props.name);

    const day = this.props.value?.day || "";
    const month = this.props.value?.month || "";
    const year = this.props.value?.year || "";
    return (
      <Question>
        <Fieldset>
          <DateLegend>
            {this.props.question} {this.props.optional ? <Optional /> : null}
            {this.props.hint ? this.props.hint : <Hint>For example, 31 08 2024</Hint>}
            {this.props.validationError ? <ErrorMessage>{this.props.validationError}</ErrorMessage> : null}
          </DateLegend>
          <DateField>
            <Label htmlFor={componentId + "_day"}>Day</Label>
            <Input
              id={componentId + "_day"}
              data-testid={testId + "-day"}
              className="dateDay"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={day}
              onChange={changeHandler?.("day")}
              onBlur={this.handleBlur("day")}
              error={!!this.props.validationError}
              disabled={this.props.disabled}
            />
          </DateField>
          <DateField>
            <Label htmlFor={componentId + "_month"}>Month</Label>
            <Input
              id={componentId + "_month"}
              data-testid={testId + "-month"}
              className="dateMonth"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={month}
              onChange={changeHandler?.("month")}
              onBlur={this.handleBlur("month")}
              error={!!this.props.validationError}
              disabled={this.props.disabled}
            />
          </DateField>
          <DateField>
            <Label htmlFor={componentId + "_year"}>Year</Label>
            <Input
              id={componentId + "_year"}
              data-testid={testId + "-year"}
              className="dateYear"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={year}
              onChange={changeHandler?.("year")}
              onBlur={this.handleBlur("year")}
              error={!!this.props.validationError}
              disabled={this.props.disabled}
            />
          </DateField>
        </Fieldset>
      </Question>
    );
  }
}

DateQuestion.propTypes = {
  question: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  // Callers fall back to "" for an unanswered field, and useDynamicFormSubmission
  // seeds each part as a number from parseExistingValue. Both reach this component.
  value: PropTypes.oneOfType([
    PropTypes.shape({
      day: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      month: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    PropTypes.string,
  ]),
  hint: PropTypes.node,
  optional: PropTypes.bool,
  disabled: PropTypes.bool,
  validationError: PropTypes.string,
  // Curried. onChange(datePart) must return an event handler, and
  // validate(datePart) must return a function that takes the field value.
  onChange: PropTypes.func,
  validate: PropTypes.func,
};

export default DateQuestion;
